import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  styled,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { AxiosError } from "axios";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import useContactPersonDetails from "../../hooks/contactPersons/useContactPersonDetails";
import useMembers from "../../hooks/members/useMembers";
import useCreateContactPersonNote from "../../hooks/contactPersons/useCreateContactPersonNote";
import useActivityMutations from "../../hooks/activities/useActivityMutations";
import { CrmContactStatus } from "../../types/companyTypes";
import {
  ActivityDto,
  ActivityRecurrenceEndType,
  ActivityRecurrenceFrequency,
  ActivityResultType,
  ActivityStatus,
  ActivityType,
  CreateActivityDto,
} from "../../types/activityTypes";
import { MembersFieldDto } from "../../types/membersTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import ActivityActionButtons from "../../components/activities/ActivityActionButtons";
import ActivityCompleteDialog from "../../components/activities/ActivityCompleteDialog";
import ActivityFormDialog from "../../components/activities/ActivityFormDialog";
import ActivityStatusChip from "../../components/activities/ActivityStatusChip";
import { getActivityTypeLabel } from "../../components/activities/activityLabels";
import ConfirmationDialog from "../../components/general/ConfirmationDialog";
import ContactPreferenceChip from "../../components/contactpersons/ContactPreferenceChip";
import PotentialFollowUpProjectChip from "../../components/contactpersons/PotentialFollowUpProjectChip";

type ActivityDialogMode = "create" | "edit" | "complete";

type ContactPersonNoteRow = {
  noteId: number | string;
  note: string;
  author: string;
  createdAt: Date | string | null;
};

const getActivityStatusNote = (activity: ActivityDto) => {
  if (activity.status === ActivityStatus.WAITING_FOR_CUSTOMER) {
    return `Wartet auf Kunde: ${activity.waitingComment?.trim()}`;
  }

  if (activity.resultType === ActivityResultType.DO_NOT_KEEP_CONTACT) {
    return `Kein Kontakt erwünscht: ${activity.resultComment?.trim()}`;
  }

  if (activity.resultType === ActivityResultType.POTENTIAL_FUTURE_PROJECT) {
    return `Potenzielles Folgeprojekt: ${activity.resultComment?.trim()}`;
  }

  return `Aktivität abgeschlossen: ${activity.resultComment?.trim()}`;
};

const ContactPersonDetails: React.FunctionComponent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const contactPersonId = Number(id);

  const styles = {
    button: {
      marginTop: "20px",
      marginBottom: "20px",
      border: "0",
      backgroundColor: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        color: "black",
      },
    },
    tableContainer: {
      maxHeight: "calc(100vh - 310px)",
    },
    tableHeadCell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    actionButtons: {
      display: "flex",
      gap: "8px",
      alignItems: "center",
      justifyContent: "flex-end",
    },
  };

  const StyledLink = styled(Link)`
    text-decoration: none;
    color: black;
    &:hover {
      text-decoration: underline;
    }
  `;

  const {
    contactPersonDetails,
    activities,
    notes,
    projects,
    isActivitiesLoading,
    isNotesLoading,
    isProjectsLoading,
    detailsError,
    activitiesError,
    notesError,
    projectsError,
  } = useContactPersonDetails(contactPersonId);

  const { members } = useMembers();
  const {
    createActivity: mutateCreateActivity,
    updateActivity: mutateUpdateActivity,
    updateActivityStatus: mutateUpdateActivityStatus,
    deleteActivity: mutateDeleteActivity,
    isCreatingActivity,
    isUpdatingActivity,
    isUpdatingActivityStatus: isCompletingActivity,
    isDeletingActivity,
    invalidateActivityQueries,
  } = useActivityMutations();
  const { auth } = useContext(AuthContext);
  const hasCrmPermissions = doesPermissionsHaveSomeOf(auth.permissions, [13]);
  const isJbtContact =
    contactPersonDetails != null &&
    contactPersonDetails.jbtContactId != null &&
    contactPersonDetails.jbtContactId === auth.userID;
  const canEditContactPerson = hasCrmPermissions || isJbtContact;
  const canAddContactPersonNote = contactPersonDetails != null;
  const isAccessDenied = detailsError instanceof AxiosError && detailsError.response?.status === 403;
  const selectableMembers: MembersFieldDto[] = members.map((member) => ({
    memberId: member.memberId,
    firstname: member.firstname,
    lastname: member.lastname,
  }));

  const notesWithActivityStatusEntries = useMemo<ContactPersonNoteRow[]>(() => {
    const activityStatusEntries = activities
      .filter(
        (activity) =>
          (activity.status === ActivityStatus.WAITING_FOR_CUSTOMER && Boolean(activity.waitingComment?.trim())) ||
          (activity.status === ActivityStatus.DONE && Boolean(activity.resultComment?.trim()))
      )
      .map((activity) => ({
        noteId: `activity-contact-status-${activity.activityId}`,
        note: getActivityStatusNote(activity),
        author: activity.createdByName,
        createdAt: activity.completedAt || activity.updatedAt,
      }));

    return [
      ...notes.map((note) => ({
        noteId: note.noteId,
        note: note.note,
        author: note.author,
        createdAt: note.createdAt,
      })),
      ...activityStatusEntries,
    ];
  }, [activities, notes]);

  const isContactPersonBlockedForActivities =
    contactPersonDetails?.crmContactAllowed === false ||
    contactPersonDetails?.crmContactStatus === CrmContactStatus.NO_FURTHER_CONTACT_DESIRED;

  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityDialogMode, setActivityDialogMode] = useState<ActivityDialogMode>("create");
  const [selectedActivity, setSelectedActivity] = useState<ActivityDto | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState<ActivityType>(ActivityType.CALL);
  const [activityTitle, setActivityTitle] = useState("");
  const [activityDescription, setActivityDescription] = useState("");
  const [selectedResponsibleUser, setSelectedResponsibleUser] = useState<MembersFieldDto | null>(null);
  const [activityDeadline, setActivityDeadline] = useState<Dayjs | null>(dayjs().add(7, "day"));
  const [activityStatus, setActivityStatus] = useState<ActivityStatus>(ActivityStatus.OPEN);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<ActivityRecurrenceFrequency>(
    ActivityRecurrenceFrequency.MONTHLY
  );
  const [recurrenceEndType] = useState<ActivityRecurrenceEndType>(ActivityRecurrenceEndType.AFTER_N_OCCURRENCES);
  const [recurrenceCount, setRecurrenceCount] = useState(3);
  const [activityTypeError, setActivityTypeError] = useState(false);
  const [responsibleUserError, setResponsibleUserError] = useState(false);
  const [deadlineError, setDeadlineError] = useState(false);
  const [recurrenceCountError, setRecurrenceCountError] = useState(false);
  const [activityTitleError, setActivityTitleError] = useState(false);
  const [resultType, setResultType] = useState<ActivityResultType | "">("");
  const [resultComment, setResultComment] = useState("");
  const [resultTypeError, setResultTypeError] = useState(false);
  const [resultCommentError, setResultCommentError] = useState(false);
  const [waitingComment, setWaitingComment] = useState("");
  const [waitingCommentError, setWaitingCommentError] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  const invalidateContactPersonActivityQueries = async () => invalidateActivityQueries(contactPersonId);

  const resetActivityForm = () => {
    setSelectedActivity(null);
    setActivityDialogMode("create");
    setActivityType(ActivityType.CALL);
    setActivityTitle("");
    setActivityDescription("");
    setSelectedResponsibleUser(
      contactPersonDetails?.jbtContactId
        ? selectableMembers.find((member) => member.memberId === contactPersonDetails.jbtContactId) || null
        : null
    );
    setActivityDeadline(dayjs().add(7, "day"));
    setActivityStatus(ActivityStatus.OPEN);
    setIsRecurring(false);
    setRecurrenceFrequency(ActivityRecurrenceFrequency.MONTHLY);
    setRecurrenceCount(3);
    setActivityTypeError(false);
    setResponsibleUserError(false);
    setDeadlineError(false);
    setRecurrenceCountError(false);
    setActivityTitleError(false);
    setResultType("");
    setResultComment("");
    setResultTypeError(false);
    setResultCommentError(false);
    setWaitingComment("");
    setWaitingCommentError(false);
  };

  const createNoteMutation = useCreateContactPersonNote(contactPersonId, () => {
    setNoteDraft("");
    setIsNoteDialogOpen(false);
  });

  const handleOpenActivityDialog = () => {
    resetActivityForm();
    setActivityDialogMode("create");
    setActivityDialogOpen(true);
  };

  const populateActivityDialog = (activity: ActivityDto, mode: "edit" | "complete") => {
    setSelectedActivity(activity);
    setActivityDialogMode(mode);
    setActivityType(activity.activityType);
    setActivityTitle(activity.title);
    setActivityDescription(activity.description || "");
    setSelectedResponsibleUser(
      selectableMembers.find((member) => member.memberId === activity.responsibleUserId) || null
    );
    setActivityDeadline(dayjs(activity.deadline));
    setActivityStatus(activity.status);
    setIsRecurring(false);
    setResultType(activity.resultType || "");
    setResultComment(activity.resultComment || "");
    setWaitingComment(activity.waitingComment || "");
    setActivityTypeError(false);
    setResponsibleUserError(false);
    setDeadlineError(false);
    setRecurrenceCountError(false);
    setActivityTitleError(false);
    setResultTypeError(false);
    setResultCommentError(false);
    setWaitingCommentError(false);
    setActivityDialogOpen(true);
  };

  const handleCloseActivityDialog = () => {
    setActivityDialogOpen(false);
    resetActivityForm();
  };

  const handleOpenDeleteDialog = (activity: ActivityDto) => {
    setSelectedActivity(activity);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedActivity(null);
  };

  const handleOpenNoteDialog = () => {
    setNoteDraft("");
    setIsNoteDialogOpen(true);
  };

  const handleCloseNoteDialog = () => {
    if (createNoteMutation.isPending) {
      return;
    }
    setIsNoteDialogOpen(false);
  };

  const handleSaveNote = () => {
    const trimmed = noteDraft.trim();
    if (!trimmed) {
      return;
    }
    createNoteMutation.mutate(trimmed);
  };

  const validateActivityDialog = () => {
    let hasError = false;

    if (activityDialogMode === "complete") {
      if (!resultType) {
        setResultTypeError(true);
        hasError = true;
      } else {
        setResultTypeError(false);
      }

      if (!resultComment.trim()) {
        setResultCommentError(true);
        hasError = true;
      } else {
        setResultCommentError(false);
      }

      return !hasError;
    }

    if (
      activityDialogMode === "edit" &&
      activityStatus === ActivityStatus.WAITING_FOR_CUSTOMER &&
      !waitingComment.trim()
    ) {
      setWaitingCommentError(true);
      hasError = true;
    } else {
      setWaitingCommentError(false);
    }

    if (!activityType) {
      setActivityTypeError(true);
      hasError = true;
    } else {
      setActivityTypeError(false);
    }

    if (!selectedResponsibleUser) {
      setResponsibleUserError(true);
      hasError = true;
    } else {
      setResponsibleUserError(false);
    }

    if (!activityTitle.trim()) {
      setActivityTitleError(true);
      hasError = true;
    } else {
      setActivityTitleError(false);
    }

    if (!activityDeadline) {
      setDeadlineError(true);
      hasError = true;
    } else {
      setDeadlineError(false);
    }

    if (
      activityDialogMode === "create" &&
      isRecurring &&
      recurrenceEndType === ActivityRecurrenceEndType.AFTER_N_OCCURRENCES &&
      recurrenceCount < 1
    ) {
      setRecurrenceCountError(true);
      hasError = true;
    } else {
      setRecurrenceCountError(false);
    }

    return !hasError;
  };

  const handleSaveActivity = async () => {
    if (!contactPersonDetails || !validateActivityDialog()) {
      return;
    }

    try {
      if (activityDialogMode === "create") {
        if (!activityDeadline || !selectedResponsibleUser) {
          return;
        }

        const payload: CreateActivityDto = {
          contactPersonId: contactPersonDetails.contactPersonId,
          responsibleUserId: selectedResponsibleUser.memberId,
          activityType,
          title: activityTitle.trim(),
          description: activityDescription.trim() || null,
          deadline: activityDeadline.toDate(),
          isRecurring,
          recurrenceFrequency: isRecurring ? recurrenceFrequency : null,
          recurrenceInterval: isRecurring ? 1 : null,
          recurrenceEndType: isRecurring ? ActivityRecurrenceEndType.AFTER_N_OCCURRENCES : null,
          recurrenceEndDate: null,
          recurrenceCount: isRecurring ? recurrenceCount : null,
        };

        await mutateCreateActivity(payload);
        await invalidateContactPersonActivityQueries();
        showSuccessMessage("Aktivität erfolgreich erstellt");
        setActivityDialogOpen(false);
        resetActivityForm();
        return;
      }

      if (activityDialogMode === "edit") {
        if (!selectedActivity || !activityDeadline || !selectedResponsibleUser) {
          return;
        }

        await mutateUpdateActivity({
          activityId: selectedActivity.activityId,
          payload: {
            activityType,
            title: activityTitle.trim(),
            description: activityDescription.trim() || null,
            responsibleUserId: selectedResponsibleUser.memberId,
            deadline: activityDeadline.toDate(),
            isRecurring: selectedActivity.isRecurring,
            recurrenceFrequency: selectedActivity.recurrenceFrequency,
            recurrenceInterval: selectedActivity.recurrenceInterval,
            recurrenceEndType: selectedActivity.recurrenceEndType,
            recurrenceEndDate: selectedActivity.recurrenceEndDate,
            recurrenceCount: selectedActivity.recurrenceCount,
          },
        });

        const nextWaitingComment =
          activityStatus === ActivityStatus.WAITING_FOR_CUSTOMER ? waitingComment.trim() || null : null;
        const statusChanged =
          activityStatus !== selectedActivity.status || nextWaitingComment !== selectedActivity.waitingComment;

        if (statusChanged) {
          await mutateUpdateActivityStatus({
            activityId: selectedActivity.activityId,
            payload: {
              status: activityStatus,
              resultType: null,
              resultComment: null,
              waitingComment: nextWaitingComment,
            },
          });
        }

        await invalidateContactPersonActivityQueries();
        showSuccessMessage("Aktivität erfolgreich aktualisiert");
        setActivityDialogOpen(false);
        resetActivityForm();
        return;
      }

      if (!selectedActivity) {
        return;
      }

      await mutateUpdateActivityStatus({
        activityId: selectedActivity.activityId,
        payload: {
          status: ActivityStatus.DONE,
          resultType: resultType || null,
          resultComment: resultComment.trim() || null,
          waitingComment: null,
        },
      });
      await invalidateContactPersonActivityQueries();
      showSuccessMessage("Aktivität erfolgreich abgeschlossen");
      setActivityDialogOpen(false);
      resetActivityForm();
    } catch {
      if (activityDialogMode === "create") {
        showErrorMessage("Aktivität konnte nicht erstellt werden");
      } else if (activityDialogMode === "edit") {
        showErrorMessage("Aktivität konnte nicht aktualisiert werden");
      } else {
        showErrorMessage("Aktivität konnte nicht abgeschlossen werden");
      }
    }
  };

  if (isAccessDenied) {
    return <Typography>Keine Berechtigung für diese Ansprechperson.</Typography>;
  }

  if (!contactPersonDetails) {
    return <Typography>Ansprechperson nicht gefunden.</Typography>;
  }

  return (
    <div>
      <Grid container spacing={1} marginBottom={"50px"}>
        <Grid item>
          {canEditContactPerson ? (
            <Button sx={styles.button} variant="contained" component={Link} to={`/ansprechpersonen/${id}/bearbeiten`}>
              Ansprechperson bearbeiten
            </Button>
          ) : (
            <Tooltip title="Nur der JBT-Kontakt kann diese Ansprechperson bearbeiten">
              <span>
                <Button sx={styles.button} variant="contained" disabled>
                  Ansprechperson bearbeiten
                </Button>
              </span>
            </Tooltip>
          )}
        </Grid>
        {hasCrmPermissions && (
          <Grid item>
            <Button
              sx={styles.button}
              variant="contained"
              onClick={handleOpenActivityDialog}
              disabled={isContactPersonBlockedForActivities}
            >
              Aktivität hinzufügen
            </Button>
          </Grid>
        )}
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Name:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{contactPersonDetails.name}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Anrede:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{contactPersonDetails.salutation}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Position:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{contactPersonDetails.position}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Telefon Mobil:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{contactPersonDetails.mobilePhone}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">E-Mail:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>{contactPersonDetails.email}</Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Geburtsdatum:</Typography>
          </Grid>
          <Grid item xs>
            <Typography>
              {contactPersonDetails.birthday ? dayjs(contactPersonDetails.birthday).format("DD.MM.YYYY") : ""}
            </Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">JBT-Kontakt:</Typography>
          </Grid>
          <Grid item xs>
            {contactPersonDetails.jbtContactId ? (
              <Typography color="secondary">
                <StyledLink to={`/gesamtuebersicht/${contactPersonDetails.jbtContactId}`}>
                  {contactPersonDetails.jbtContactName}
                </StyledLink>
              </Typography>
            ) : (
              <Typography>{contactPersonDetails.jbtContactName}</Typography>
            )}
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Unternehmen:</Typography>
          </Grid>
          <Grid item xs>
            <Typography color="secondary">
              <StyledLink to={`/unternehmen/${contactPersonDetails.companyId}`}>
                {contactPersonDetails.companyName}
              </StyledLink>
            </Typography>
          </Grid>
        </Grid>
        <Grid item container alignItems="center" spacing={0} xs={12}>
          <Grid item sx={{ width: "200px" }}>
            <Typography variant="body1">Kontaktstatus:</Typography>
          </Grid>
          <Grid item xs>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <ContactPreferenceChip
                crmContactAllowed={contactPersonDetails.crmContactAllowed}
                crmContactStatus={contactPersonDetails.crmContactStatus}
                size="medium"
              />
              <PotentialFollowUpProjectChip
                visible={contactPersonDetails.crmContactStatus === CrmContactStatus.POTENTIAL_FUTURE_PROJECT}
                size="medium"
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <Typography variant="h6" marginBottom={"8px"}>
        Aktivitäten
      </Typography>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table stickyHeader aria-label="activities table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeadCell}>Typ</TableCell>
              <TableCell sx={styles.tableHeadCell}>Titel</TableCell>
              <TableCell sx={styles.tableHeadCell}>Verantwortlich</TableCell>
              <TableCell sx={styles.tableHeadCell}>Deadline</TableCell>
              <TableCell sx={styles.tableHeadCell}>Status</TableCell>
              {hasCrmPermissions && <TableCell sx={styles.tableHeadCell}>Aktionen</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {activitiesError ? (
              <TableRow>
                <TableCell colSpan={hasCrmPermissions ? 6 : 5}>Aktivitäten konnten nicht geladen werden.</TableCell>
              </TableRow>
            ) : isActivitiesLoading ? (
              <TableRow>
                <TableCell colSpan={hasCrmPermissions ? 6 : 5}>Aktivitäten werden geladen …</TableCell>
              </TableRow>
            ) : activities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={hasCrmPermissions ? 6 : 5}>Keine Aktivitäten vorhanden.</TableCell>
              </TableRow>
            ) : (
              activities.map((activity) => (
                <TableRow
                  hover
                  key={activity.activityId}
                  onClick={() => navigate(`/ansprechpersonen/${id}/aktivitaeten/${activity.activityId}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{getActivityTypeLabel(activity.activityType)}</TableCell>
                  <TableCell>
                    <Typography color="secondary">
                      <StyledLink
                        to={`/ansprechpersonen/${id}/aktivitaeten/${activity.activityId}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {activity.title}
                      </StyledLink>
                    </Typography>
                  </TableCell>
                  <TableCell>{activity.responsibleUserName}</TableCell>
                  <TableCell>{dayjs(activity.deadline).format("DD.MM.YYYY")}</TableCell>
                  <TableCell>
                    <ActivityStatusChip status={activity.status} />
                  </TableCell>
                  {hasCrmPermissions && (
                    <TableCell sx={{ textAlign: "right" }}>
                      <Box sx={styles.actionButtons}>
                        <ActivityActionButtons
                          isDone={activity.status === ActivityStatus.DONE}
                          onComplete={() => populateActivityDialog(activity, "complete")}
                          onEdit={() => populateActivityDialog(activity, "edit")}
                          onDelete={() => handleOpenDeleteDialog(activity)}
                          stopPropagation
                        />
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" alignItems="center" spacing={2} marginTop={"24px"} marginBottom={"8px"}>
        <Typography variant="h6">Notizen</Typography>
        {canAddContactPersonNote && (
          <Button size="small" variant="outlined" onClick={handleOpenNoteDialog}>
            Notiz hinzufügen
          </Button>
        )}
      </Stack>
      <TableContainer component={Paper}>
        <Table aria-label="notes table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeadCell}>Notiz</TableCell>
              <TableCell sx={styles.tableHeadCell}>Autor + Erstelldatum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notesError ? (
              <TableRow>
                <TableCell colSpan={2}>Notizen konnten nicht geladen werden.</TableCell>
              </TableRow>
            ) : isNotesLoading ? (
              <TableRow>
                <TableCell colSpan={2}>Notizen werden geladen …</TableCell>
              </TableRow>
            ) : notesWithActivityStatusEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2}>Keine Notizen vorhanden.</TableCell>
              </TableRow>
            ) : (
              notesWithActivityStatusEntries.map((note) => (
                <TableRow hover key={note.noteId}>
                  <TableCell>{note.note}</TableCell>
                  <TableCell>{`${note.author} am ${dayjs(note.createdAt).format("DD.MM.YYYY")}`}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" marginTop={"24px"} marginBottom={"8px"}>
        Zugeordnete Projekte
      </Typography>
      <TableContainer component={Paper}>
        <Table aria-label="assigned projects table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeadCell}>Projektname</TableCell>
              <TableCell sx={styles.tableHeadCell}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projectsError ? (
              <TableRow>
                <TableCell colSpan={2}>Projekte konnten nicht geladen werden.</TableCell>
              </TableRow>
            ) : isProjectsLoading ? (
              <TableRow>
                <TableCell colSpan={2}>Projekte werden geladen …</TableCell>
              </TableRow>
            ) : projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2}>Keine Projekte zugeordnet.</TableCell>
              </TableRow>
            ) : (
              projects.map((project) => (
                <TableRow hover key={project.projectId}>
                  <TableCell>{project.projectName}</TableCell>
                  <TableCell>{project.status}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Grid item container marginBottom={"50px"}></Grid>

      {activityDialogMode === "complete" ? (
        <ActivityCompleteDialog
          open={activityDialogOpen}
          onClose={handleCloseActivityDialog}
          onSubmit={handleSaveActivity}
          isSubmitting={isCreatingActivity || isUpdatingActivity || isCompletingActivity}
          resultType={resultType}
          onResultTypeChange={(value) => {
            setResultType(value);
            setResultTypeError(false);
          }}
          resultComment={resultComment}
          onResultCommentChange={(value) => {
            setResultComment(value);
            if (value.trim()) {
              setResultCommentError(false);
            }
          }}
          resultTypeError={resultTypeError}
          resultCommentError={resultCommentError}
        />
      ) : (
        <ActivityFormDialog
          open={activityDialogOpen}
          title={activityDialogMode === "create" ? "Aktivität hinzufügen" : "Aktivität bearbeiten"}
          submitLabel={activityDialogMode === "create" ? "Aktivität erstellen" : "Änderungen speichern"}
          onClose={handleCloseActivityDialog}
          onSubmit={handleSaveActivity}
          isSubmitting={isCreatingActivity || isUpdatingActivity || isCompletingActivity}
          contactPersonName={contactPersonDetails.name}
          companyName={contactPersonDetails.companyName}
          activityType={activityType}
          onActivityTypeChange={(value) => {
            setActivityType(value);
            setActivityTypeError(false);
          }}
          activityTypeError={activityTypeError}
          activityTitle={activityTitle}
          onActivityTitleChange={(value) => {
            setActivityTitle(value);
            setActivityTitleError(false);
          }}
          activityTitleError={activityTitleError}
          activityDescription={activityDescription}
          onActivityDescriptionChange={setActivityDescription}
          responsibleUserId={selectedResponsibleUser?.memberId || ""}
          onResponsibleUserChange={(value) => {
            setSelectedResponsibleUser(selectableMembers.find((member) => member.memberId === value) || null);
            setResponsibleUserError(false);
          }}
          responsibleUserError={responsibleUserError}
          responsibleUserLabel="Verantwortliche:r JBTler"
          members={selectableMembers}
          activityDeadline={activityDeadline}
          onActivityDeadlineChange={(value) => {
            setActivityDeadline(value);
            setDeadlineError(value === null);
          }}
          deadlineError={deadlineError}
          idPrefix="activity"
          statusOptions={
            activityDialogMode === "edit"
              ? {
                  value: activityStatus,
                  onChange: (value) => {
                    setActivityStatus(value);
                    if (value !== ActivityStatus.WAITING_FOR_CUSTOMER) {
                      setWaitingComment("");
                      setWaitingCommentError(false);
                    }
                  },
                  options:
                    selectedActivity?.status === ActivityStatus.DONE
                      ? [{ value: ActivityStatus.DONE, label: "Erledigt" }]
                      : [
                          { value: ActivityStatus.OPEN, label: "Offen" },
                          { value: ActivityStatus.WAITING_FOR_CUSTOMER, label: "Wartet auf Kunde" },
                        ],
                  disabled: selectedActivity?.status === ActivityStatus.DONE,
                }
              : undefined
          }
          waitingReasonOptions={
            activityDialogMode === "edit"
              ? {
                  value: waitingComment,
                  onChange: (value) => {
                    setWaitingComment(value);
                    if (value.trim()) {
                      setWaitingCommentError(false);
                    }
                  },
                  error: waitingCommentError,
                }
              : undefined
          }
          createRecurrenceOptions={
            activityDialogMode === "create"
              ? {
                  isRecurring,
                  onIsRecurringChange: setIsRecurring,
                  recurrenceFrequency,
                  onRecurrenceFrequencyChange: setRecurrenceFrequency,
                  recurrenceCount,
                  onRecurrenceCountChange: setRecurrenceCount,
                  recurrenceCountError,
                }
              : undefined
          }
        />
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={async () => {
          if (selectedActivity) {
            try {
              await mutateDeleteActivity(selectedActivity.activityId);
              await invalidateContactPersonActivityQueries();
              showSuccessMessage("Aktivität erfolgreich gelöscht");
              setDeleteDialogOpen(false);
              resetActivityForm();
            } catch {
              showErrorMessage("Aktivität konnte nicht gelöscht werden");
            }
          }
        }}
        cofirmButtonColor="error"
        title="Aktivität löschen"
        content={`Möchtest du die Aktivität${
          selectedActivity?.title ? ` "${selectedActivity.title}"` : ""
        } wirklich löschen?`}
        confirmButtonText={isDeletingActivity ? "Lösche..." : "Löschen"}
      />

      <Dialog open={isNoteDialogOpen} onClose={handleCloseNoteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Neue Notiz</DialogTitle>
        <DialogContent>
          <TextField
            id="contact-person-note"
            name="contact-person-note"
            autoFocus
            margin="dense"
            label="Notiz"
            fullWidth
            multiline
            minRows={3}
            value={noteDraft}
            onChange={(event) => setNoteDraft(event.target.value)}
            disabled={createNoteMutation.isPending}
          />
          {createNoteMutation.isError && (
            <Typography color="error" variant="body2" marginTop={1}>
              Notiz konnte nicht gespeichert werden.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoteDialog} disabled={createNoteMutation.isPending}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSaveNote}
            variant="contained"
            disabled={!noteDraft.trim() || createNoteMutation.isPending}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContactPersonDetails;
